/**
 * Turning what the user types in a time entry (date + start time + duration)
 * into the exact start/end instants the API stores, and back -- in whole
 * minutes/seconds, in the user's LOCAL time, with no decimal hours anywhere.
 * The server derives `hours` from these; nothing here sends it.
 */

const pad = (n: number) => n.toString().padStart(2, '0');

/** YYYY-MM-DD of a Date in LOCAL time (toISOString() would give the UTC date). */
export function localDateString(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** HH:MM of a Date in LOCAL time, 24h. */
export function localTimeString(date: Date): string {
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * "6:15" -> 375, "0615" -> 375, "2" -> 2, "125" -> 85 (1:25), "" -> null.
 * A bare number is minutes-and-hours the way the field has always read: the last two digits are minutes.
 */
export function parseDurationMinutes(input: string): number | null {
  let value = String(input ?? '').trim();
  if (!value) return null;
  if (!value.includes(':')) {
    const digits = value.replace(/\D/g, '');
    if (!digits) return null;
    const padded = digits.length >= 3 ? digits : digits.padStart(4, '0');
    value = `${padded.slice(0, -2)}:${padded.slice(-2)}`;
  }
  const [h, m] = value.split(':');
  const hours = parseInt(h, 10) || 0;
  const minutes = parseInt(m, 10) || 0;
  return hours * 60 + minutes;
}

/** 90 -> "01:30" (the Duration field's own format). */
export function durationFieldValue(totalSeconds: number): string {
  const totalMinutes = Math.round(Math.max(0, totalSeconds) / 60);
  return `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}`;
}

/** Date + start time + minutes -> the exact ISO instants (start local, end = start + minutes). */
export function composeEntryTimes(date: string, startTime: string, durationMinutes: number): { startISO: string; endISO: string } {
  const start = new Date(`${date}T${startTime}`);
  const end = new Date(start.getTime() + durationMinutes * 60 * 1000);
  return { startISO: start.toISOString(), endISO: end.toISOString() };
}
